class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this._overlayCloseHandler = this.close.bind(this);
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this._overlayCloseHandler);
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  bindOverlayClose() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (!overlay) return;
    overlay.removeEventListener('click', this._overlayCloseHandler);
    overlay.addEventListener('click', this._overlayCloseHandler);
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }
    if (typeof removeTrapFocus === 'function') removeTrapFocus();
    setTimeout(() => this.classList.add('animate', 'active'));
    this.addEventListener(
      'transitionend',
      () => {
        const container = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusEl = this.querySelector('.drawer__close') || this.querySelector('.drawer__inner');
        if (container && typeof trapFocus === 'function') trapFocus(container, focusEl);
      },
      { once: true }
    );
    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');
    if (cartDrawerNote.nextElementSibling?.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }
    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });
    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  getSectionInnerHTML(html, selector) {
    if (!html) return '';
    const root = new DOMParser().parseFromString(html, 'text/html');
    const target = root.querySelector(selector || '.shopify-section');
    return target ? target.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }

  // Called by product-form.js and custom-product-form.js after add-to-cart
  renderContents(parsedState) {
    return CartDrawer.updateFromState(parsedState, this);
  }

  static updateFromState(parsedState, cartDrawerEl) {
    cartDrawerEl = cartDrawerEl || document.querySelector('cart-drawer');
    if (!parsedState || !parsedState.sections || !cartDrawerEl) return Promise.resolve();

    cartDrawerEl.classList.remove('is-empty');
    const drawerInner = cartDrawerEl.querySelector('.drawer__inner');
    if (drawerInner) drawerInner.classList.remove('is-empty');

    // Replace the inner #CartDrawer content (includes header, shipping bar, items, footer)
    const drawerSectionHTML = parsedState.sections['cart-drawer'];
    if (drawerSectionHTML) {
      const cartDrawerRoot = document.getElementById('CartDrawer');
      if (cartDrawerRoot) {
        const parsed = new DOMParser().parseFromString(drawerSectionHTML, 'text/html');
        const newContent = parsed.querySelector('#CartDrawer');
        if (newContent) {
          cartDrawerRoot.innerHTML = newContent.innerHTML;
        }
      }
    }

    // Replace cart icon bubble
    const bubbleHTML = parsedState.sections['cart-icon-bubble'];
    if (bubbleHTML) CartDrawer.replaceCartIconBubble(bubbleHTML);

    // Re-bind overlay after innerHTML replacement
    cartDrawerEl.bindOverlayClose();

    // Notify shipping JS that the DOM has been updated, then open
    return new Promise((resolve) => {
      requestAnimationFrame(() => {
        // shipping update runs AFTER new HTML is in the DOM
        if (typeof window.onCartDrawerRefreshed === 'function') {
          window.onCartDrawerRefreshed(parsedState);
        }

        if (!cartDrawerEl.classList.contains('active')) {
          cartDrawerEl.open();
        }

        resolve();
      });
    });
  }

  static replaceCartIconBubble(html) {
    if (!html) return;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const source =
      parsed.querySelector('#cart-icon-bubble') ||
      parsed.querySelector('[data-cart-icon-bubble]') ||
      parsed.querySelector('.shopify-section');
    const targets = document.querySelectorAll('#cart-icon-bubble, [data-cart-icon-bubble]');
    if (!source || !targets.length) return;
    const sourceInner = source.querySelector('#cart-icon-bubble') || source;
    targets.forEach((target) => (target.innerHTML = sourceInner.innerHTML));
  }

  static getSectionNames() {
    return 'cart-drawer,cart-icon-bubble';
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

window.updateCartDrawerFromState = CartDrawer.updateFromState;
window.getCartDrawerSectionNames = CartDrawer.getSectionNames;

// ─── CartDrawerItems ──────────────────────────────────────────────────────────
// Completely independent from the main CartItems class to avoid any dependency
// on main-cart-items / main-cart-footer which don't exist on the collection page.

class CartDrawerItems extends CartItems {
  // Only sections that exist inside the cart drawer
  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }

  enableLoading(line) {
    const cartItemsEl = document.getElementById('CartDrawer-CartItems');
    if (cartItemsEl) cartItemsEl.classList.add('cart__items--disabled');

    this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`).forEach((overlay) =>
      overlay.classList.remove('hidden')
    );

    document.activeElement.blur();

    // lineItemStatusElement may be null when the cart drawer HTML was just replaced
    const statusEl =
      this.lineItemStatusElement || document.getElementById('CartDrawer-LineItemStatus');
    if (statusEl) statusEl.setAttribute('aria-hidden', false);
  }

  disableLoading() {
    const cartItemsEl = document.getElementById('CartDrawer-CartItems');
    if (cartItemsEl) cartItemsEl.classList.remove('cart__items--disabled');
  }

  updateQuantity(line, quantity, name) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: ['cart-drawer', 'cart-icon-bubble'],
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);

        const cartDrawerWrapper = document.querySelector('cart-drawer');
        if (cartDrawerWrapper) {
          cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);
        }

        // Replace full #CartDrawer HTML so header count, shipping bar,
        // items list, footer subtotal all update in one shot
        return CartDrawer.updateFromState(parsedState, cartDrawerWrapper).then(() => {
          // Dispatch after DOM update so listeners get new total_price
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: parsedState }));

          this.updateLiveRegions(line, parsedState.item_count);

          // Try to keep focus on the quantity input that was changed
          const lineItem = document.getElementById(`CartDrawer-Item-${line}`);
          if (lineItem && name && lineItem.querySelector(`[name="${name}"]`)) {
            if (cartDrawerWrapper && typeof trapFocus === 'function') {
              trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`));
            }
          } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
            const emptyEl = cartDrawerWrapper.querySelector('.drawer__inner-empty');
            const anchor = cartDrawerWrapper.querySelector('a');
            if (emptyEl && typeof trapFocus === 'function') trapFocus(emptyEl, anchor);
          }

          this.disableLoading();
        });
      })
      .catch(() => {
        this.querySelectorAll('.loading-overlay').forEach((o) => o.classList.add('hidden'));
        const errors = document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings?.error || '';
        this.disableLoading();
      });
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
